import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js'
import {isLeft} from 'fp-ts/lib/These'
import * as t from 'io-ts'
import {
  BU_COGNITO_APP_ID,
  BU_COGNITO_POOL_ID,
  ROUTING_API_URL,
  SFMC_PLUGIN_URL,
  WORKBENCH_CLIENT_URL
} from './env'
import {createNanoEvents} from './nanoevents'

const CLIENT_ID_KEY = 'authClientId'

const getLocalClientId = () => localStorage.getItem(CLIENT_ID_KEY)

const setLocalClientId = (clientId: string) =>
  localStorage.setItem(CLIENT_ID_KEY, clientId)

interface AuthStateAnon {
  authenticated: false
  loaded: boolean
  error?: string
}
interface UserInfo {
  sub: string
  username: string
  email: string
  mfaEnabled: boolean
  ssoLogin: boolean
  aud: string
}
export interface AuthStateAuth extends UserInfo {
  authenticated: true
  loaded: true
}

export type AuthState = AuthStateAnon | AuthStateAuth

type Event = 'stateChange' | 'signOut'

type Payload = {
  [key: string]: string | number | boolean
}
interface Events {
  stateChange: (authState: AuthState, payload?: Payload) => void
  signOut: () => void
}

export type SignInResult =
  | {state: 'success' | 'confirm'}
  | {
      state: 'totp'
      sendTOTPCode: (code: string) => Promise<void>
    }

type SignInFunc = (username: string, password: string) => Promise<SignInResult>

type SignUpFunc = (
  username: string,
  password: string
) => Promise<{state: 'success' | 'confirm'}>

type ConfirmFunc = (username: string, code: string) => Promise<boolean>

type ResendCodeFunc = (username: string) => Promise<boolean>

type HandleAuthFunc = (
  code: string
) => Promise<{success: true} | {success: false; error: string}>

type SetClientIdFunc = (
  clientId: string | null
) => Promise<{success: true} | {success: false; error: string}>

// extract useful information from a CognitoUser into a simple object
const sanitiseCognitoUser = async (user: CognitoUser): Promise<UserInfo> => {
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession) => {
      if (err) {
        reject(err)
      } else {
        const token = session.getIdToken()
        resolve({
          sub: token.payload.sub,
          username: token.payload['cognito:username'],
          email: token.payload.email,
          mfaEnabled: token.payload.mfa_enabled === 'true',
          ssoLogin: 'identities' in token.payload,
          aud: token.payload.aud
        })
      }
    })
  })
}

// these need to be allowed in the cognito app client (LogoutURLs)
// https://github.com/atomic-app/environments/blob/development/cfn-masters/cognitos/lambda/cfn-stack-cognitos-pre-package.yaml#L405
type KnownSignOutPaths = '/' | '/demo-login.html'

export interface Authenticator {
  on: (
    event: Event,
    handler: (state: AuthState, payload?: Payload) => void | Promise<void>,
    immediate?: boolean
  ) => () => void
  signIn: SignInFunc
  signUp: SignUpFunc
  signOut: (redirect?: KnownSignOutPaths) => void
  getAuthState: () => AuthState
  refreshAuthState: () => Promise<void>
  getIdToken: () => Promise<string>
  confirm: ConfirmFunc
  resendCode: ResendCodeFunc
  pool: CognitoUserPool
  handleAuthCode: HandleAuthFunc
  setClientId: SetClientIdFunc
  getClientId: () => string
}

export class CognitoError extends Error {}

export const parseJsonOrThrow = async (
  response: Response,
  fallbackMessage: string
): Promise<unknown> => {
  try {
    return await response.json()
  } catch {
    throw new CognitoError(fallbackMessage)
  }
}

const TokensCodec = t.type({
  id_token: t.string,
  access_token: t.string
})

const SignInResponseCodec = t.union([
  t.type({state: t.literal('success'), tokens: TokensCodec}),
  t.type({state: t.literal('confirm')}),
  t.type({
    state: t.literal('mfa'),
    challengeName: t.union([
      t.literal('SMS_MFA'),
      t.literal('SOFTWARE_TOKEN_MFA')
    ]),
    session: t.string,
    username: t.string
  })
])

const RespondToChallengeResponseCodec = t.type({tokens: TokensCodec})

const SignUpResponseCodec = t.type({
  state: t.union([t.literal('success'), t.literal('confirm')])
})

export const makeAuthenticator = (
  appFlavour: 'workbench' | 'sfmc-plugin'
): Authenticator => {
  // use custom client app if found, to enable SSO, defaulting to primary
  const getClientId = () => getLocalClientId() || BU_COGNITO_APP_ID
  const makePool = () =>
    new CognitoUserPool({
      UserPoolId: BU_COGNITO_POOL_ID,
      ClientId: getClientId()
    })

  let pool: CognitoUserPool = makePool()

  const reinitialisePool = async () => {
    pool = makePool()
    await emit('stateChange')
  }

  let authState: AuthState = {authenticated: false, loaded: false}

  const emitter = createNanoEvents<Events>()

  const getAuthState = () => authState

  const setAuthState = async () => {
    const user = pool.getCurrentUser()
    let newAuthState: AuthState
    if (user) {
      try {
        const userDetails = await sanitiseCognitoUser(user)
        newAuthState = {authenticated: true, loaded: true, ...userDetails}
      } catch (e) {
        console.error(e)
        newAuthState = {authenticated: false, loaded: true}
      }
    } else {
      newAuthState = {authenticated: false, loaded: true}
    }
    // Only create a new object if newAuthState has changed
    // This can prevent unnecessary renders via the AuthProvider
    if (JSON.stringify(newAuthState) !== JSON.stringify(authState)) {
      authState = newAuthState
    }
  }

  const emit = async (event: Event, payload?: Payload) => {
    await setAuthState()
    emitter.emit(event, authState, payload)
  }

  emit('stateChange')

  /** Create a Cognito session from tokens returned by the routing-api and persist it to localStorage.
   * `user.setSignInUserSession` stores the session in localStorage via the Cognito SDK so that
   * subsequent calls to `pool.getCurrentUser()` and `user.getSession()` pick it up. */
  const createSessionFromTokens = (tokens: t.TypeOf<typeof TokensCodec>) => {
    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({IdToken: tokens.id_token}),
      // Refresh token is stored server-side as an httpOnly cookie — pass empty
      // string here so the SDK doesn't crash; user.refreshSession() is never
      // called since getIdToken() uses the /auth/refresh proxy instead.
      RefreshToken: new CognitoRefreshToken({RefreshToken: ''}),
      AccessToken: new CognitoAccessToken({
        AccessToken: tokens.access_token
      })
    })

    const user = new CognitoUser({
      Pool: pool,
      Username: session.getIdToken().decodePayload()['cognito:username']
    })

    user.setSignInUserSession(session)
    return user
  }

  // React StrictMode (and re-renders of the /auth callback page) can invoke
  // handleAuthCode twice with the same authorization code. Cognito codes are
  // single-use: a second exchange returns invalid_grant AND triggers Cognito's
  // code-reuse detection, which revokes the tokens issued to the first exchange,
  // leaving the user with a broken session (e.g. no organisations). Dedupe by
  // code so a given code is only ever exchanged once.
  let authCodeExchange: {
    code: string
    promise: ReturnType<HandleAuthFunc>
  } | null = null

  const handleAuthCode: HandleAuthFunc = code => {
    if (authCodeExchange?.code === code) {
      return authCodeExchange.promise
    }
    const promise = exchangeAuthCode(code)
    authCodeExchange = {code, promise}
    return promise
  }

  const exchangeAuthCode: HandleAuthFunc = async (code: string) => {
    // Exchange the authorization code via the routing-api server-side proxy
    // instead of calling the Cognito token endpoint directly from the browser.
    const url = `${ROUTING_API_URL}/auth/exchange-code`
    // Derive redirectUri from the same env-configured base used to build the
    // authorize URL (see SignInForm.ssoRedirect). Cognito requires the token
    // exchange redirect_uri to match the authorize redirect_uri byte-for-byte;
    // using window.location.origin here risked a mismatch across custom domains.
    const redirectUriBase =
      appFlavour === 'sfmc-plugin'
        ? `${SFMC_PLUGIN_URL}/auth.html`
        : `${WORKBENCH_CLIENT_URL}/auth`
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code,
        clientId: getClientId(),
        redirectUri: `${redirectUriBase}?client_id=${getClientId()}`
      })
    })

    if (response.status >= 300) {
      return {
        success: false,
        error: 'Invalid response from auth server'
      }
    }

    let raw: unknown
    try {
      raw = await response.json()
    } catch {
      return {
        success: false,
        error: 'Invalid response from auth server'
      }
    }
    const result = TokensCodec.decode(raw)
    if (isLeft(result)) {
      console.error('Auth code exchange response failed codec validation')
      return {
        success: false,
        error: 'Invalid response from auth server'
      }
    }

    createSessionFromTokens(result.right)

    await emit('stateChange')

    return {success: true}
  }

  const setClientId: SetClientIdFunc = async clientId => {
    if (getClientId() !== clientId) {
      setLocalClientId(clientId || '')
      await reinitialisePool()
    }
    return {success: true}
  }

  const signIn: SignInFunc = async (email: string, password: string) => {
    const response = await fetch(`${ROUTING_API_URL}/auth/sign-in`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        username: email,
        password
      })
    })

    const raw = await parseJsonOrThrow(response, 'Sign in failed')

    if (response.status >= 300) {
      throw new CognitoError(
        (raw as {error?: string}).error ?? 'Sign in failed'
      )
    }

    const result = SignInResponseCodec.decode(raw)
    if (isLeft(result)) {
      throw new CognitoError('Unexpected response from auth server')
    }

    const data = result.right

    if (data.state === 'confirm') {
      return {state: 'confirm'}
    }

    if (data.state === 'success') {
      createSessionFromTokens(data.tokens)
      await emit('stateChange')
      return {state: 'success'}
    }

    // MFA challenge — return a sendTOTPCode callback that calls the respond-to-challenge proxy
    const {session, challengeName, username} = data
    return {
      state: 'totp',
      sendTOTPCode: async (code: string) => {
        const challengeResponse = await fetch(
          `${ROUTING_API_URL}/auth/respond-to-challenge`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
              session,
              code,
              challengeName,
              username
            })
          }
        )

        const challengeRaw = await parseJsonOrThrow(
          challengeResponse,
          'MFA verification failed'
        )

        if (challengeResponse.status >= 300) {
          throw new CognitoError(
            (challengeRaw as {error?: string}).error ??
              'MFA verification failed'
          )
        }

        const challengeResult =
          RespondToChallengeResponseCodec.decode(challengeRaw)
        if (isLeft(challengeResult)) {
          throw new CognitoError('Unexpected response from auth server')
        }

        createSessionFromTokens(challengeResult.right.tokens)
        await emit('stateChange')
      }
    }
  }

  const signUp: SignUpFunc = async (email: string, password: string) => {
    const response = await fetch(`${ROUTING_API_URL}/auth/sign-up`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        username: email,
        password
      })
    })

    const raw = await parseJsonOrThrow(response, 'Sign up failed')

    if (response.status >= 300) {
      throw new CognitoError(
        (raw as {error?: string}).error ?? 'Sign up failed'
      )
    }

    const result = SignUpResponseCodec.decode(raw)
    if (isLeft(result)) {
      throw new CognitoError('Unexpected response from auth server')
    }

    return {state: result.right.state}
  }

  const confirm: ConfirmFunc = async (
    username: string,
    verificationCode: string
  ) => {
    const response = await fetch(`${ROUTING_API_URL}/auth/confirm`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        username,
        code: verificationCode
      })
    })

    const raw = await parseJsonOrThrow(response, 'Confirmation failed')

    if (response.status >= 300) {
      throw new CognitoError(
        (raw as {error?: string}).error ?? 'Confirmation failed'
      )
    }

    return true
  }

  const resendCode: ResendCodeFunc = async (username: string) => {
    const response = await fetch(`${ROUTING_API_URL}/auth/resend-code`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        username
      })
    })

    const raw = await parseJsonOrThrow(response, 'Resend code failed')

    if (response.status >= 300) {
      throw new CognitoError(
        (raw as {error?: string}).error ?? 'Resend code failed'
      )
    }

    return true
  }

  const signOut = async (redirectPath: KnownSignOutPaths = '/') => {
    // Clear the server-side httpOnly refresh token cookie and retrieve the
    // Cognito hosted-UI logout URL (needed to clear SSO session cookies on
    // the Cognito domain — cannot be done server-side).
    let logoutUrl = window.origin + redirectPath
    try {
      const response = await fetch(`${ROUTING_API_URL}/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          clientId: getClientId(),
          logoutUri: window.origin + redirectPath
        })
      })
      if (response.ok) {
        const data = await response.json()
        if (typeof data?.logoutUrl === 'string') logoutUrl = data.logoutUrl
      }
    } catch (_e) {} // eslint-disable-line no-empty

    const user = pool.getCurrentUser()
    if (user) {
      user.signOut()
    }

    setLocalClientId('')

    await emit('signOut')
    await emit('stateChange')

    // Redirect to Cognito logout endpoint to clear SSO session cookies on the
    // Cognito domain — without this, SSO users can silently re-authenticate.
    // setTimeout gives time to send any analytics etc.
    setTimeout(() => (window.location.href = logoutUrl), 500) // eslint-disable-line functional/immutable-data
  }

  let refreshInFlight: Promise<string> | null = null

  const startRefresh = (): Promise<string> => {
    if (!refreshInFlight) {
      refreshInFlight = (async () => {
        const response = await fetch(`${ROUTING_API_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        })
        if (!response.ok) {
          throw new Error('Session expired, please sign in again')
        }
        let raw: unknown
        try {
          raw = await response.json()
        } catch {
          throw new Error('Invalid refresh response from auth server')
        }
        const result = TokensCodec.decode(raw)
        if (isLeft(result)) {
          throw new Error('Invalid refresh response from auth server')
        }
        createSessionFromTokens(result.right)
        return result.right.id_token
      })().finally(() => {
        refreshInFlight = null
      })
    }
    return refreshInFlight
  }

  const getIdToken = (forceRefresh = false): Promise<string> => {
    const user = pool.getCurrentUser()

    if (!user) {
      return Promise.reject('User not logged in')
    }

    return new Promise((resolve, reject) => {
      user.getSession((err: Error | null, session: CognitoUserSession) => {
        // If getSession throws (e.g. expired/corrupt localStorage session), the
        // httpOnly refresh cookie may still be valid — try the refresh proxy
        // before giving up.
        if (err) {
          return startRefresh().then(resolve).catch(reject)
        }
        if (!forceRefresh && session.isValid()) {
          return resolve(session.getIdToken().getJwtToken())
        }
        startRefresh().then(resolve).catch(reject)
      })
    })
  }

  return {
    on: (event, handler, immediate = false) => {
      if (immediate) {
        handler(authState)
      }
      return emitter.on(event, handler)
    },
    signIn,
    signUp,
    signOut,
    getAuthState,
    refreshAuthState: async () => {
      await getIdToken(true)
    },
    getIdToken,
    confirm,
    resendCode,
    pool,
    handleAuthCode,
    setClientId,
    getClientId
  }
}
